import { FC, useState, useEffect } from 'react';
import { Box, VStack, HStack, Text, Button, Image, Grid, Badge } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { photoService } from '@/services/firebase/photoService';
import { DogPhoto } from '@/types/dog';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface PhotoGalleryProps {
  dogId: string;
  userId: string;
  onPhotosChange?: () => void;
}

/**
 * 写真ギャラリーコンポーネント
 */
export const PhotoGallery: FC<PhotoGalleryProps> = ({
  dogId,
  userId,
  onPhotosChange,
}) => {
  const [photos, setPhotos] = useState<DogPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<DogPhoto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DogPhoto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSettingMain, setIsSettingMain] = useState(false);

  /**
   * 写真一覧を取得
   */
  const fetchPhotos = async () => {
    setLoading(true);
    setError(null);

    try {
      const fetchedPhotos = await photoService.getPhotos(dogId, userId);
      setPhotos(fetchedPhotos);
    } catch (err) {
      console.error('写真取得エラー:', err);
      setError(err instanceof Error ? err.message : '写真の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, [dogId, userId]);

  /**
   * 写真削除
   */
  const handleDeletePhoto = async (photo: DogPhoto) => {
    setIsDeleting(true);

    try {
      await photoService.deletePhoto(photo.id, dogId, userId);
      await fetchPhotos();
      setDeleteTarget(null);

      if (onPhotosChange) {
        onPhotosChange();
      }
    } catch (err) {
      console.error('写真削除エラー:', err);
      setError(err instanceof Error ? err.message : '写真の削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * メイン写真設定
   */
  const handleSetMainPhoto = async (photo: DogPhoto) => {
    setIsSettingMain(true);

    try {
      await photoService.setMainPhoto(photo.id, dogId, userId);
      await fetchPhotos();

      if (onPhotosChange) {
        onPhotosChange();
      }
    } catch (err) {
      console.error('メイン写真設定エラー:', err);
      setError(err instanceof Error ? err.message : 'メイン写真の設定に失敗しました');
    } finally {
      setIsSettingMain(false);
    }
  };

  /**
   * ローディング表示
   */
  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Text color="gray.500">読み込み中...</Text>
      </Box>
    );
  }

  /**
   * エラー表示
   */
  if (error) {
    return (
      <Box p={4} bg="red.50" borderRadius="md" borderWidth={1} borderColor="red.200">
        <VStack gap={2}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
          <Button size="sm" onClick={fetchPhotos}>
            再試行
          </Button>
        </VStack>
      </Box>
    );
  }

  /**
   * 空状態
   */
  if (photos.length === 0) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="md">
        <Text color="gray.500">写真がありません</Text>
      </Box>
    );
  }

  return (
    <VStack gap={4} align="stretch">
      {/* 写真グリッド */}
      <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
        {photos.map((photo) => (
          <Box
            key={photo.id}
            position="relative"
            borderRadius="lg"
            overflow="hidden"
            borderWidth={photo.isMain ? 3 : 1}
            borderColor={photo.isMain ? 'blue.500' : 'gray.200'}
            transition="all 0.2s"
            _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
          >
            {/* メインバッジ */}
            {photo.isMain && (
              <Badge
                position="absolute"
                top={2}
                left={2}
                colorPalette="blue"
                fontSize="xs"
                zIndex={1}
              >
                メイン
              </Badge>
            )}

            {/* 写真 */}
            <Image
              src={photo.fileUrl}
              alt={photo.fileName}
              w="100%"
              h="200px"
              objectFit="cover"
              cursor="pointer"
              onClick={() => setSelectedPhoto(photo)}
            />

            {/* アクションボタン */}
            <Box
              position="absolute"
              bottom={0}
              left={0}
              right={0}
              p={2}
              bg="rgba(0, 0, 0, 0.7)"
              opacity={0}
              transition="opacity 0.2s"
              _groupHover={{ opacity: 1 }}
            >
              <HStack gap={2} justify="center">
                {!photo.isMain && (
                  <Button
                    size="xs"
                    colorPalette="blue"
                    onClick={() => handleSetMainPhoto(photo)}
                    disabled={isSettingMain}
                  >
                    メインに設定
                  </Button>
                )}
                <Button
                  size="xs"
                  colorPalette="red"
                  onClick={() => setDeleteTarget(photo)}
                >
                  削除
                </Button>
              </HStack>
            </Box>

            {/* アップロード日時 */}
            <Box p={2} bg="white">
              <Text fontSize="xs" color="gray.500">
                {format(photo.uploadedAt.toDate(), 'yyyy/MM/dd HH:mm', { locale: ja })}
              </Text>
            </Box>
          </Box>
        ))}
      </Grid>

      {/* 拡大表示モーダル */}
      {selectedPhoto && (
        <DialogRoot
          open={!!selectedPhoto}
          onOpenChange={() => setSelectedPhoto(null)}
          size="xl"
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{selectedPhoto.fileName}</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Image
                src={selectedPhoto.fileUrl}
                alt={selectedPhoto.fileName}
                w="100%"
                maxH="70vh"
                objectFit="contain"
              />
              <Text fontSize="sm" color="gray.500" mt={2}>
                アップロード日時:{' '}
                {format(selectedPhoto.uploadedAt.toDate(), 'yyyy年M月d日 HH:mm', {
                  locale: ja,
                })}
              </Text>
            </DialogBody>
            <DialogFooter>
              <DialogActionTrigger asChild>
                <Button variant="outline">閉じる</Button>
              </DialogActionTrigger>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}

      {/* 削除確認ダイアログ */}
      {deleteTarget && (
        <DialogRoot
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>写真の削除</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <Text>この写真を削除してもよろしいですか？</Text>
              {deleteTarget.isMain && (
                <Text fontSize="sm" color="orange.600" mt={2}>
                  メイン写真を削除すると、次の写真が自動的にメイン写真になります。
                </Text>
              )}
            </DialogBody>
            <DialogFooter>
              <HStack gap={2}>
                <DialogActionTrigger asChild>
                  <Button variant="outline" disabled={isDeleting}>
                    キャンセル
                  </Button>
                </DialogActionTrigger>
                <Button
                  colorPalette="red"
                  onClick={() => handleDeletePhoto(deleteTarget)}
                  loading={isDeleting}
                >
                  削除
                </Button>
              </HStack>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}
    </VStack>
  );
};
